import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardContainer from '../components/layout/DashboardContainer';
import CalendarSettings from '../components/settings/CalendarSettings';
import { fetchClientProfile, updateClientProfile, disconnectFacebook, switchPhoneNumber } from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { launchWhatsAppEmbeddedSignup } from '../utils/metaEmbeddedSignup';
import './SettingsPage.css';

export default function SettingsPage() {
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileForm, setProfileForm] = useState({
    name: '',
    owner_phone_number: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await fetchClientProfile();
      const data = response.data;
      setProfile(data);
      setProfileForm({
        name: data?.name || '',
        owner_phone_number: data?.owner_phone_number || data?.owner_phone || '',
      });
      setError('');
    } catch (err) {
      setError('Failed to load profile. Please try again.');
      console.error('[SettingsPage] Error loading profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();

    const handleMessage = (event) => {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin + '/api/v1';
      const expectedOrigin = new URL(apiUrl).origin;
      if (event.origin === expectedOrigin || event.origin === window.location.origin) {
        if (event.data && event.data.type === 'oauth_success') {
          setTimeout(() => { loadProfile(); }, 1500);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleStatus = params.get('google');
    if (!googleStatus) return;

    if (googleStatus === 'connected') {
      showSuccess('Google Calendar connected successfully.');
      loadProfile();
    } else if (googleStatus === 'failed') {
      showError('Google Calendar connection failed. Please try again.');
    }

    params.delete('google');
    params.delete('reason');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, showError, showSuccess]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError('');

    try {
      await launchWhatsAppEmbeddedSignup();
      showSuccess('WhatsApp Business Account connected successfully.');
      await loadProfile();
    } catch (err) {
      const message = err.message || 'Failed to connect your WhatsApp Business Account.';
      setError(message);
      showError(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Disconnect your WhatsApp account? This will stop all message processing.')) {
      try {
        await disconnectFacebook();
        loadProfile();
      } catch (err) {
        setError('Failed to disconnect. Please try again.');
      }
    }
  };

  const handlePhoneNumberSwitch = async (phoneNumberId) => {
    try {
      await switchPhoneNumber(phoneNumberId);
      setError('');
      loadProfile();
    } catch (err) {
      setError('Failed to switch phone number.');
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const { data } = await updateClientProfile({
        name: profileForm.name,
        owner_phone_number: profileForm.owner_phone_number,
      });
      setProfile(data);
      setProfileForm({
        name: data?.name || '',
        owner_phone_number: data?.owner_phone_number || data?.owner_phone || '',
      });
      showSuccess('Profile updated successfully.');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const isConnected = profile && profile.phone_number_id;
  const hasMultiplePhones = profile?.available_phone_numbers?.length > 1;

  return (
    <DashboardContainer>
      <div className="settings-page">
        <h1>Settings</h1>
        <p>Manage your account and integrations.</p>

        <div className="settings-card">
          <h2>Business Profile</h2>

          {isLoading ? (
            <div>
              <div className="skeleton line" style={{ width: '55%', marginBottom: 10 }} />
              <div className="skeleton line" style={{ width: '40%', marginBottom: 10 }} />
              <div className="skeleton line" style={{ width: '65%' }} />
            </div>
          ) : (
            <div className="profile-section">
              <div className="profile-grid">
                <div>
                  <label className="profile-label">Business Name</label>
                  <input
                    className="input"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <label className="profile-label">Owner Phone Number</label>
                  <input
                    className="input"
                    value={profileForm.owner_phone_number}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, owner_phone_number: e.target.value }))}
                    placeholder="Enter your WhatsApp number"
                  />
                </div>
                <div>
                  <label className="profile-label">Login Email</label>
                  <input
                    className="input"
                    value={profile?.email || 'Not set'}
                    disabled
                  />
                </div>
                <div>
                  <label className="profile-label">Login Username</label>
                  <input
                    className="input"
                    value={profile?.username || 'Not set'}
                    disabled
                  />
                </div>
                <div>
                  <label className="profile-label">Connected WhatsApp Number</label>
                  <input
                    className="input"
                    value={profile?.whatsapp_number || 'Not connected'}
                    disabled
                  />
                </div>
                <div>
                  <label className="profile-label">Subscription Plan</label>
                  <input
                    className="input"
                    value={profile?.plan || 'starter'}
                    disabled
                  />
                </div>
              </div>
              <div className="button-group">
                <button className="btn primary" onClick={handleProfileSave} disabled={savingProfile}>
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="settings-card">
          <h2>WhatsApp Integration</h2>

          {isLoading && (
            <div>
              <div className="skeleton line" style={{ width: '50%', marginBottom: 10 }} />
              <div className="skeleton line" style={{ width: '30%' }} />
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}

          {!isLoading && !error && (
            <>
              {isConnected ? (
                <div className="connection-status connected">
                  <p>
                    <strong>Status:</strong>
                    <span className="status-badge status-active">Connected</span>
                  </p>
                  <p><strong>WhatsApp Number:</strong> {profile.whatsapp_number || 'N/A'}</p>

                  {hasMultiplePhones && (
                    <div className="phone-number-selector">
                      <p><strong>Available Phone Numbers:</strong></p>
                      <p className="muted-sm">Select which number to use for messaging:</p>
                      {profile.available_phone_numbers.map((phone) => (
                        <div
                          key={phone.id}
                          className={`phone-option ${phone.id === profile.phone_number_id ? 'active' : ''}`}
                          onClick={() => phone.id !== profile.phone_number_id && handlePhoneNumberSwitch(phone.id)}
                        >
                          <strong>{phone.display_phone_number || phone.verified_name}</strong>
                          {phone.verified_name && phone.display_phone_number && (
                            <span className="muted-sm" style={{ marginLeft: 8 }}>({phone.verified_name})</span>
                          )}
                          {phone.id === profile.phone_number_id && (
                            <span className="badge success" style={{ marginLeft: 8 }}>✓ Active</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="button-group">
                    <button onClick={handleConnect} className="btn btn-secondary" disabled={isConnecting}>
                      {isConnecting ? 'Connecting...' : 'Reconnect with Meta'}
                    </button>
                    <button onClick={handleDisconnect} className="btn btn-danger">Disconnect</button>
                  </div>
                </div>
              ) : (
                <div className="connection-status disconnected">
                  <p>
                    <strong>Status:</strong>
                    <span className="status-badge status-inactive">Not Connected</span>
                  </p>
                  <p className="muted-sm">Connect your WhatsApp Business Account to send and receive messages.</p>
                  <div className="button-group">
                    <button onClick={handleConnect} className="btn primary" disabled={isConnecting}>
                      {isConnecting ? 'Connecting...' : 'Connect with Meta'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <h2>Google Calendar Scheduler</h2>
          <p className="muted-sm" style={{ marginBottom: 12 }}>
            Connect Google Calendar so the AI agent can suggest live availability, collect the customer's email, and book meetings automatically.
          </p>
          <CalendarSettings />
        </div>
      </div>
    </DashboardContainer>
  );
}
